import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { X } from "lucide-react";

type Props = {
  title: string;
  submitLabel: string;
  /** Runs when the form is submitted; it must handle its own errors (the shell only tracks the wait of an async one). */
  onSubmit: () => void | Promise<void>;
  onClose: () => void;
  submitDisabled?: boolean;
  /** Red submit button, for «Atmesti». */
  danger?: boolean;
  children: React.ReactNode;
};

/** Window chrome shared by the asset request dialogs: title, body, «Atšaukti» and one submit button. */
export default function DialogShell({
  title,
  submitLabel,
  onSubmit,
  onClose,
  submitDisabled = false,
  danger = false,
  children,
}: Props) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={submitting ? () => {} : onClose} className="relative z-[1000]">
      <DialogBackdrop className="fixed inset-0 bg-zinc-950/50" />
      <div className="fixed inset-0 flex items-start justify-center p-2 sm:p-6">
        <DialogPanel className="mt-8 w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-zinc-950/10">
          <form onSubmit={submit} className="flex max-h-[85dvh] flex-col">
            <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
              <DialogTitle className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
                {title}
              </DialogTitle>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                aria-label={t("close")}
                className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 max-md:flex max-md:size-11 max-md:items-center max-md:justify-center"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">{children}</div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 max-md:min-h-11"
              >
                {t("cancel")}
              </button>
              <button
                type="submit"
                disabled={submitDisabled || submitting}
                className={clsx(
                  "rounded-lg px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 max-md:min-h-11",
                  danger ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"
                )}
              >
                {submitLabel}
              </button>
            </div>
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
